import { bem } from '~/common/classname';
import { Play as PlayIcon } from '~/highlight/icons';
import type { RecordType } from '~/highlight/types/record';
import { formatTwoDigitNumber } from '~/highlight/utils/number';
import { fetchStatistics } from '~/common/api/fetchStatistics';
import './styles.scss';

const COMMON_CLASSNAME = 'bai-highlight-entrance';

type Props = {
  target: HTMLElement;
  data: RecordType[];
  title: string;
  logo?: string;
  buttonText?: string;
  onClick?: (record: RecordType) => void;
  [key: string]: any;
};

class Entrance {
  props: Props;
  onListItemClick: (event: MouseEvent) => void;
  onButtonClick: () => void;

  constructor(props: Props) {
    this.props = { ...props };
    this.onListItemClick = this.handleListItemClick.bind(this);
    this.onButtonClick = this.handleButtonClick.bind(this);
    this.render();
  }

  template() {
    const { data, logo, buttonText } = this.props;
    return /*html*/ `
    <div class="${bem([COMMON_CLASSNAME, 'header'])}">
      <div class="${bem([COMMON_CLASSNAME, 'header', 'label'])}">
        ${
          logo
            ? /*html*/ `<img alt="" src="${logo}" class="${bem([
                COMMON_CLASSNAME,
                'header',
                'logo',
              ])}" />`
            : ''
        }
        <h2 class="${bem([
          COMMON_CLASSNAME,
          'header',
          'title',
        ])}"><span>AI</span> ${this.props.title}</h2>
        </div>
        <button class="${bem([
          COMMON_CLASSNAME,
          'header',
          'button',
        ])}">${buttonText}</button>
    </div>
    <ol class="${bem([COMMON_CLASSNAME, 'content'])}">
      ${data
        .slice(0, 3)
        .map((record) => {
          const { id, subject, thumbnail, properties } = record;
          const marker = properties['marker']?.value ?? [];
          const start = marker[0]?.value ?? 0;
          const decimal = Math.trunc(start);
          const hh = Math.floor(decimal / (60 * 60));
          const mm = Math.floor(decimal / 60);
          const ss = decimal % 60;
          return /*html*/ `
            <li class="${bem([COMMON_CLASSNAME, 'list-item'])} ${
            !!logo
              ? bem([bem([COMMON_CLASSNAME, 'list-item']), 'thumb-space'], true)
              : ''
          }" role="button" data-item-id="${id}">
              <div class="${bem([COMMON_CLASSNAME, 'list-item', 'thumbnail'])}">
                <img src="${thumbnail}" alt="" />
                <div class="${bem([
                  COMMON_CLASSNAME,
                  'list-item',
                  'play-icon',
                ])}">
                  ${PlayIcon}
                </div>
              </div>
              <div class="${bem([
                COMMON_CLASSNAME,
                'list-item',
                'information',
              ])}">
                <p>${subject}</p>
                <p>${hh > 0 ? hh.toString() + ':' : ''}${formatTwoDigitNumber(
            mm
          )}:${formatTwoDigitNumber(ss)}</p>
              </div>
            </li>
          `;
        })
        .join('')}
    </ol>
    <div class="${bem([COMMON_CLASSNAME, 'notice'])}">
        <p>AI를 통해 자동 생성된 내용으로 방송 내용과 차이가 있을 수 있습니다.</p>
    </div>
    `;
  }

  render() {
    const { target } = this.props;
    target.innerHTML = this.template();
    this.setup();
  }

  handleListItemClick(event: MouseEvent) {
    const li = (event.target as HTMLElement).closest(
      `li.${bem([COMMON_CLASSNAME, 'list-item'])}`
    );

    if (!li) return;

    const id = li.getAttribute('data-item-id');
    const data = this.props.data.find((record) => `${record.id}` === id);

    if (!data) return;

    this.props.onClick(data);

    (async () => {
      const { shop } = this.props;
      const {
        categories = [],
        pid,
        pdid,
        title,
      } = data.properties.product.value;
      await fetchStatistics({
        path: '/stat/highlight_video/click',
        frame: 'product',
        area: 'ai_highlight',
        company: shop || 'oksoon',
        pd_type: 'tv_link',
        rank: data.id + 1,
        pd_name: title,
        pdid,
        pid,
        category1: categories[0],
        category2: categories[1],
        category3: categories[2],
      });
    })();
  }

  handleButtonClick() {
    const first = this.props.data[0];
    this.props.onClick(first);
  }

  setup() {
    const ol = document.querySelector(`.${bem([COMMON_CLASSNAME, 'content'])}`);
    ol?.addEventListener('click', this.onListItemClick);

    const button = document.querySelector(
      `.${bem([COMMON_CLASSNAME, 'header', 'button'])}`
    );
    button?.addEventListener('click', this.onButtonClick);
  }
}

export default Entrance;
